import { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { VacationBalances } from '~/data/absences';
import { useEmployeeAbsenceData } from '~/data/absences/useEmployeeAbsenceData';
import { useApplicationConfig } from '~/data/applicationConfig';
import { useCompanyData } from '~/data/company/useCompanyData';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { Roles } from '~/data/user';
import { useSelectedEmployeeId } from '~/hooks/employee';
import { useSWR } from '~/hooks/useSWR';
import { useVisible } from '~/hooks/useVisible';

import { getVacHistoryTableData, vacationDaysHistoryData } from './data';

export type UseFormsOutput = {
  defaultData?: VacationBalances;
  vacHistoryTableData: vacationDaysHistoryData[];
  bnEnableVacBank: boolean;
  vacBankType1: boolean;
  vacBankType2: boolean;
  vacBankType3: boolean;
  openDiaolog: boolean;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
  isAbsHistoryDialogOpen: boolean;
  closeAbsHistoryDialog: () => void;
  openAbsHistoryDialog: () => void;
  extraVacEnum: AbsEnum[];
  absShortReasonEnum: AbsEnum[];
  vacBankEnum: AbsEnum[];
  editMode: boolean;
  setEditMode: Dispatch<SetStateAction<boolean>>;
  hasEditAccess: boolean;
  selectedEmployeeId: number;
  onUpdate: () => void;
};

export type AbsEnum = {
  code: string;
  extCode?: string;
  name: string;
  description?: string;
};

export const useVacationBalanceData = (): UseFormsOutput => {
  // Load configs
  const { applicationConfig } = useApplicationConfig();
  const { companyData } = useCompanyData();
  const { bnEnableVacBank, vacBankType1, vacBankType2, vacBankType3 } =
    useMemo(() => {
      const absenceConfig = applicationConfig?.fComConfig?.absConfig;
      const leaveTypes = companyData?.config?.fComLeaveTypes;

      return {
        bnEnableVacBank: !!absenceConfig?.fComEnableVacationBank,
        vacBankType1: leaveTypes?.fComVacationBankLeaveTypes1?.length === 0,
        vacBankType2: leaveTypes?.fComVacationBankLeaveTypes2?.length === 0,
        vacBankType3: leaveTypes?.fComVacationBankLeaveTypes3?.length === 0,
      };
    }, [applicationConfig, companyData]);

  const { getEnums } = useAllEnums();
  const { vacBankEnum, extraVacEnum, absShortReasonEnum } = useMemo(() => {
    return {
      vacBankEnum: getEnums(EnumListKey.VACATION_BANK_LABEL),
      extraVacEnum: getEnums(EnumListKey.VACATION_BALANCE_LABEL),
      absShortReasonEnum: getEnums(EnumListKey.ABSENCE_SHORT_REASON),
    };
  }, [getEnums]);

  // Selected employee & access
  const {
    selectedEmployeeId,
    selectedEmployeeDetails: { hasRoleAccessOverEmployee, isMyselfWithRole },
  } = useSelectedEmployeeId();

  const balanceEditorRoles = [
    Roles.HR_ADMIN,
    Roles.HR_ADMIN_LIMITED,
    Roles.HR_STAFF,
    Roles.HR_DEV,
  ];
  const hasEditAccess =
    hasRoleAccessOverEmployee(balanceEditorRoles) ||
    isMyselfWithRole(balanceEditorRoles);

  // Load absence data
  const { absencesData, mutate } = useEmployeeAbsenceData({
    employeeId: selectedEmployeeId,
  });

  const { defaultData } = useMemo(() => {
    const defaultData = absencesData?.vacationBalance;

    return {
      defaultData,
    };
  }, [absencesData]);

  const { data: vacHistoryResponse } = useSWR(
    ['getVacHistoryTableData', selectedEmployeeId.toString()],
    ([_key, employeeId]) => getVacHistoryTableData(employeeId),
  );
  const vacHistoryTableData = vacHistoryResponse as vacationDaysHistoryData[];

  // States
  const [openDiaolog, setOpenDialog] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);

  const {
    isVisible: isAbsHistoryDialogOpen,
    show: openAbsHistoryDialog,
    hide: closeAbsHistoryDialog,
  } = useVisible();

  return {
    defaultData,
    vacHistoryTableData,
    bnEnableVacBank,
    vacBankType1,
    vacBankType2,
    vacBankType3,
    openDiaolog,
    setOpenDialog,
    isAbsHistoryDialogOpen,
    closeAbsHistoryDialog,
    openAbsHistoryDialog,
    extraVacEnum,
    absShortReasonEnum,
    editMode: hasEditAccess && editMode,
    setEditMode,
    vacBankEnum,
    hasEditAccess,
    selectedEmployeeId,
    onUpdate: () => {
      mutate();
    },
  };
};
